"use client"

import BreadCrumb from "@/components/breadcrumb"
import NormalContent from "@/components/normal-content"
import RelatedPostDetail from "@/components/related-post-detail"
import SocialList from "@/components/social-list"
import TableContent from "@/components/table-content"
import { t } from "@/languages/i18n"
import { DefaultImage } from "@/lib/external-components"
import { useNotificationMessages } from "@/lib/useNotificationMessages"
import { generateImagePath } from "@/lib/utils"
import { extractTableOfContents } from "@/utils/extract-table-of-contents"
import Image from "next/image"
import { useEffect, useMemo, useState } from "react"
import { PostData } from "../lib/config/types"

type Props = {
  postData?: PostData
  hasBreadcrumb?: boolean
}

const PagePostDetail = ({ postData, hasBreadcrumb }: Props) => {
  const { contextHolderNotification } = useNotificationMessages()
  const lang = useMemo(() => {
    if (typeof document !== "undefined") return document.documentElement.lang || "en"
    return "en"
  }, [])

  const [share, setShare] = useState<{ url: string; title: string }>({ url: "", title: "" })
  const [showTOCButton, setShowTOCButton] = useState(false)
  const [isTOCOpen, setIsTOCOpen] = useState(false)

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return ""
    return new Intl.DateTimeFormat("vi-VN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(dateStr))
  }

  const breadcrumbItems = [
    {
      title: "Blog",
      link: lang === "en" ? "/blogs" : "/blogs-vi",
    },
    {
      title: postData?.attributes.name,
      link: "",
    },
  ]

  const content = postData?.attributes?.content || ""
  const { toc: tableOfContents, htmlWithIds } = useMemo(() => extractTableOfContents(content), [content])

  useEffect(() => {
    setShare({
      url: encodeURIComponent(window.location.href),
      title: encodeURIComponent(document.title || ""),
    })
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const imageElement = document.getElementById("post-featured-image")
      if (imageElement) {
        const imageBottom = imageElement.getBoundingClientRect().bottom
        setShowTOCButton(imageBottom < 0)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <div>
      {contextHolderNotification}
      {hasBreadcrumb && <BreadCrumb items={breadcrumbItems} />}

      <div className="max-w-[1200px] mx-auto flex flex-col">
        <div className="pt-4 sm:pt-8 relative">
          <div className="flex flex-col gap-16 relative sm:pb-[2rem] pb-10">
            <div className="flex flex-col gap-6 items-start max-w-[1000px] relative lg:px-0 px-4">
              {postData?.attributes?.tags?.data &&
                postData?.attributes?.tags?.data?.map((tag, index) => (
                  <p
                    key={index}
                    className="px-2 py-1.5 rounded-lg bg-[#0B9444] uppercase text-white font-semibold text-sm leading-4 inline-block text-center"
                  >
                    {tag.attributes.name}
                  </p>
                ))}
              <h1 className="text-[#242F3E] font-bold text-5xl leading-[56px]">{postData?.attributes.name}</h1>
              <div
                dangerouslySetInnerHTML={{ __html: postData?.attributes.excerpt || "" }}
                className="text-xl font-normal text-[#4F4F4F]"
              />
              <div className="flex flex-row gap-16 items-center">
                <div className="flex flex-col gap-1 items-start">
                  <p className="uppercase text-[#999999] font-normal text-sm leading-5">{t("Written by")}</p>
                  <p className="text-[#242F3E] font-normal text-base leading-5 ">{t("Manager")}</p>
                </div>
                <div className="flex flex-col gap-1 items-start">
                  <p className="uppercase text-[#999999] font-normal text-sm leading-5 ">{t("Publish on")}</p>
                  <p className="text-[#242F3E] font-normal text-base leading-5 ">
                    {formatDate(postData?.attributes.updatedAt)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start gap-16 relative">
              <div className="hidden sm:flex flex-col gap-6 sticky top-24 lg:px-0 px-4">
                {tableOfContents.length > 0 && (
                  <div className="flex flex-col justify-center gap-4 ">
                    <h2 className="sm:text-sm sm:leading-5 font-normal uppercase text-[#999999]">
                      {t("Table of contents")}
                    </h2>
                    <TableContent items={tableOfContents} />
                  </div>
                )}
                <SocialList currentUrl={share.url} currentTitle={share.title} />
              </div>

              <div className="flex flex-col sm:max-w-[888px] w-full">
                <div className="flex flex-col sm:gap-16 gap-10 justify-center border-b border-[#EBEDF0] pb-16">
                  <figure className="sm:rounded-2xl overflow-hidden">
                    <div id="post-featured-image" className="relative w-full h-[228px] sm:h-[499px]">
                      <Image
                        src={
                          postData?.attributes?.image?.data?.attributes?.url
                            ? generateImagePath(postData?.attributes?.image?.data?.attributes?.url, "original")
                            : DefaultImage
                        }
                        alt={
                          postData?.attributes?.image?.data?.attributes?.alternativeText ||
                          postData?.attributes.name ||
                          ""
                        }
                        fill
                        className="object-cover w-full h-full sm:rounded-2xl"
                      />
                    </div>
                    {postData?.attributes?.image?.data?.attributes?.caption && (
                      <figcaption className="text-center text-sm mt-2 text-gray-500 italic">
                        {postData?.attributes?.image?.data?.attributes?.caption}
                      </figcaption>
                    )}
                  </figure>

                  {/* Mobile TOC - appears after image */}
                  {tableOfContents.length > 0 && (
                    <div className="sm:hidden px-4">
                      <div className="flex flex-col gap-4 p-4 bg-[#F9F9FB] rounded-lg">
                        <h2 className="text-sm leading-5 font-semibold uppercase text-[#242F3E]">
                          {t("Table of contents")}
                        </h2>
                        <TableContent items={tableOfContents} />
                      </div>
                    </div>
                  )}

                  <NormalContent content={htmlWithIds} tableOfContents={tableOfContents} />
                </div>
                <div className="w-full sm:hidden flex flex-col gap-4 items-start px-4 pt-10">
                  <div className="flex flex-col gap-1 items-start">
                    <p className="uppercase text-[#999999] font-normal text-sm leading-5">{t("Written by")}</p>
                    <p className="text-[#242F3E] font-normal text-base leading-5 ">{t("Manager")}</p>
                  </div>
                  <div className="flex flex-col gap-4 items-start">
                    <p className="uppercase text-[#999999] font-normal text-sm leading-5">{t("Share this post")}</p>
                    <SocialList currentUrl={share.url} currentTitle={share.title} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating TOC Button for Mobile */}
      {tableOfContents.length > 0 && showTOCButton && (
        <button
          onClick={() => setIsTOCOpen(true)}
          className="sm:hidden fixed bottom-6 left-6 z-40 bg-[#0B9444] text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 font-semibold text-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Mobile TOC Modal */}
      {isTOCOpen && (
        <div className="sm:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsTOCOpen(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#242F3E]">{t("Table of contents")}</h2>
              <button onClick={() => setIsTOCOpen(false)} className="text-[#999999] hover:text-[#242F3E]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <TableContent items={tableOfContents} />
          </div>
        </div>
      )}

      <section className="bg-[#F9F9FB]">
        <RelatedPostDetail pageSize="8" currentPostId={postData?.id} locale={lang} />
      </section>
    </div>
  )
}

export default PagePostDetail
